import { useCallback, useMemo, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Info, Shield } from 'lucide-react';
import { SessionDefaultsInheritanceChainRows } from './SessionDefaultsInheritanceChain';
import { getTrackOwnerUserIdFromMetadata, type Track } from '@/types/prioritization/track';
import type { ResultResolutionOverride } from '@/types/prioritization/track';
import type { DegapProcess } from '@/types/prioritization/degapCycle';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useReadinessDefinitionStore } from '@/stores/prioritization/readinessDefinitionStore';
import {
  workspaceLayerFromWorkspace,
  cycleLayerFromCycle,
  trackLayerFromTrack,
  getSessionDefaultsInheritanceBreakdown,
  type SessionTimingMode,
  type SessionEntryMode,
} from '@/lib/effectiveConfigResolver';
import { useEffectiveTrackParticipants } from '@/hooks/useEffectiveTrackParticipants';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useSessionPolicyStore, type UpsertSessionPolicyInput } from '@/stores/prioritization/sessionPolicyStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { RankOrderChangeSetting } from './RankOrderChangeSetting';
import { RoomRulesSettings } from './RoomRulesSettings';
import { fetchSessionDefaults, withServerAbove, type SessionDefaults } from '@/lib/sessionDefaults';
import { usePublishedTrackResult } from '@/lib/tracks/publishedTrackResult';
import { Select } from '@/components/ds';

export interface TrackSessionDefaultsSectionProps {
  track: Track;
  cycle: DegapProcess | null;
}

export function TrackSessionDefaultsSection({ track, cycle }: TrackSessionDefaultsSectionProps) {
  const updateTrack = useTrackStore((s) => s.updateTrack);
  const applyPolicyToTrackLocal = useTrackStore((s) => s.applyPolicyToTrackLocal);
  const upsertPolicy = useSessionPolicyStore((s) => s.upsertPolicy);
  const [busy, setBusy] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(true);

  const { effectiveUserIds } = useEffectiveTrackParticipants(track.cycleId, track.id);
  const { users } = useSystemUsers();
  const facilitatorOptions = useMemo(
    () => (effectiveUserIds.length > 0 ? users.filter((u) => effectiveUserIds.includes(u.id)) : []),
    [effectiveUserIds, users]
  );

  // Phase C-2 (canon 28 §6, migration 598): timing / entry / facilitator and
  // the four allowSession* toggles are Policy content — written to
  // session_policies ('track' scope), then re-applied to the local track so
  // the UI reflects the save. resultResolution stays a tracks column.
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const trackPolicyWorkspaceId =
    track.workspaceId ?? cycle?.workspaceId ?? currentWorkspaceId ?? null;

  const savePolicy = useCallback(
    async (changes: Omit<UpsertSessionPolicyInput, 'scopeKind' | 'scopeId'>) => {
      setSaveError(null);
      const saved = await upsertPolicy({ scopeKind: 'track', scopeId: track.id, ...changes });
      if (saved) applyPolicyToTrackLocal(track.id);
      else setSaveError(useSessionPolicyStore.getState().error ?? 'The setting could not be saved.');
    },
    [track.id, upsertPolicy, applyPolicyToTrackLocal]
  );

  const handleChange = useCallback(
    async (field: string, value: string) => {
      const next = value === '' ? null : value;
      setBusy(field);
      try {
        if (field === 'timing') {
          await savePolicy({ timingMode: next as SessionTimingMode | null });
        } else if (field === 'entry') {
          await savePolicy({ entryMode: next as SessionEntryMode | null });
        } else if (field === 'facilitator') {
          await savePolicy({ defaultFacilitatorId: next });
        } else if (field === 'resultResolution') {
          await updateTrack(track.id, { resultResolutionOverride: next as ResultResolutionOverride | null });
        }
      } finally {
        setBusy(null);
      }
    },
    [track.id, updateTrack, savePolicy]
  );

  const getCurrentWorkspace = useTenantStore((s) => s.getCurrentWorkspace);
  const can = useUserStore((s) => s.can);
  const userId = useUserStore((s) => s.userId);
  // The same two paths set_session_policy allows (migration 1012).
  const trackOwnerId = getTrackOwnerUserIdFromMetadata(track.metadata) ?? cycle?.ownerId ?? null;
  const canConfigureTrack = can('tracks.configure') || (Boolean(userId) && userId === trackOwnerId);
  // S-22: the workflow decides whether a track may choose its own result rule
  // (canon 05 allow_track_resolution_override); the server ignores the track's
  // choice otherwise, so the control is not drawn.
  const { result: publishedResult } = usePublishedTrackResult(track.id, track.updatedAt);
  const trackMayChooseResult = publishedResult?.trackOverrideAllowed === true;

  // ── Readiness definition inheritance ────────────────────────────────────────
  const rdStore = useReadinessDefinitionStore();
  useEffect(() => {
    const ws = getCurrentWorkspace();
    if (ws?.id && rdStore.definitions.length === 0 && !rdStore.loading) {
      rdStore.loadForWorkspace(ws.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // What sits above the track's cycle is the server's answer (S-40,
  // get_session_defaults from 'workflow'), the workflow included.
  const [above, setAbove] = useState<SessionDefaults | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchSessionDefaults({ cycleId: track.cycleId ?? null, trackId: track.id }, 'workflow').then(({ defaults }) => {
      if (!cancelled) setAbove(defaults);
    });
    return () => { cancelled = true; };
  }, [track.id, track.cycleId, cycle?.workflowId]);

  const breakdown = useMemo(() => {
    const workspace = getCurrentWorkspace();
    const workspaceLayer = workspaceLayerFromWorkspace(workspace ?? null);
    const cycleLayer = cycleLayerFromCycle(cycle);
    const trackLayer = trackLayerFromTrack(track);
    const facilitatorLabel = (id: string) => users.find((u) => u.id === id)?.name || users.find((u) => u.id === id)?.email || 'Unknown user';
    return withServerAbove(
      getSessionDefaultsInheritanceBreakdown(workspaceLayer ?? null, cycleLayer ?? null, trackLayer ?? null, null, { facilitatorLabel }),
      above,
      facilitatorLabel,
    );
  }, [getCurrentWorkspace, cycle, track, users, above]);

  const timingValue = track.defaultTimingMode ?? '';
  const entryValue = track.defaultEntryMode ?? '';
  const currentFacilitatorId = track.defaultFacilitatorId ?? '';
  const facilitatorValue =
    currentFacilitatorId && effectiveUserIds.includes(currentFacilitatorId) ? currentFacilitatorId : '';
  const resultResolutionValue = track.resultResolutionOverride ?? '';

  const allowSessionOverride: boolean =
    typeof track.metadata?.allowSessionOverride === 'boolean'
      ? (track.metadata.allowSessionOverride as boolean)
      : true;

  // S-43: the three "Allow session participants to …" switches are not drawn.
  // Nothing enforces them yet; their session_policies columns stay.

  const handleAllowSessionOverrideChange = useCallback(
    async (value: boolean) => {
      setBusy('allowSessionOverride');
      try {
        await savePolicy({ allowSessionOverride: value });
      } finally {
        setBusy(null);
      }
    },
    [savePolicy]
  );

  const settings = [
    {
      key: 'timing',
      label: 'Session mode',
      value: timingValue,
      effective: breakdown.timingMode,
      options: [
        { value: '', label: 'Inherit' },
        { value: 'live', label: 'Live' },
        { value: 'async', label: 'Async' },
      ],
    },
    {
      key: 'entry',
      label: 'Data entry',
      value: entryValue,
      effective: breakdown.entryMode,
      options: [
        { value: '', label: 'Inherit' },
        { value: 'facilitator', label: 'Facilitator' },
        { value: 'participants', label: 'Participants' },
      ],
    },
    {
      key: 'facilitator',
      label: 'Facilitator',
      value: facilitatorValue,
      effective: breakdown.defaultFacilitatorId,
      options: [
        { value: '', label: 'Inherit' },
        ...facilitatorOptions.map((u) => ({ value: u.id, label: u.name || u.email || u.id })),
      ],
      warning: facilitatorOptions.length === 0 ? 'No participants on this track yet.' : undefined,
    },
    {
      key: 'resultResolution',
      label: 'Which result counts',
      locked: trackMayChooseResult ? undefined : 'Set by the workflow. A track chooses its own only when the workflow allows it (Workflows → Tracks may choose their own).',
      value: resultResolutionValue,
      effective: {
        global: 'From workflow',
        cycle: '—',
        track: resultResolutionValue ? resultResolutionValue.replace(/_/g, ' ') : '—',
        session: '—',
        effective: resultResolutionValue ? resultResolutionValue.replace(/_/g, ' ') : 'From workflow',
        source: (resultResolutionValue ? 'track' : 'global') as 'global' | 'cycle' | 'track' | 'session',
      },
      options: [
        { value: '', label: 'Inherit' },
        { value: 'latest_published', label: 'Latest published' },
        { value: 'selected_official', label: 'Chosen session' },
        // S-22: combining results is not built yet — shown, never chosen.
        { value: 'aggregate', label: 'Combined (not available yet)', disabled: true },
      ],
    },
  ];

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Session defaults</h3>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">Inherited unless overridden</span>
      </div>

      <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
        {settings.map((s) => {
          const isOverridden = s.value !== '';
          return (
            <div key={s.key} className="px-4 py-2.5 flex items-center gap-4">
              <span className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] w-28 shrink-0">{s.label}</span>

{'locked' in s && s.locked ? (
                <span className="text-sm text-[var(--text-sec)]">{s.locked}</span>
              ) : !canConfigureTrack ? (
                isOverridden && (
                  <span className="text-sm text-[var(--text-pri)]">
                    {s.options.find((o) => o.value === s.value)?.label ?? s.value}
                  </span>
                )
              ) : (
                            <Select
                value={s.value}
                onChange={(e) => handleChange(s.key, e.target.value)}
                disabled={busy !== null}
                className={`text-sm rounded border px-2.5 py-1.5 min-w-[140px] focus:border-[var(--slate)] focus:ring-1 focus:ring-blue-500 dark:bg-[var(--surface)] dark:text-[var(--text-pri)] ${
                  isOverridden
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)]/50 text-[var(--text-pri)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/20'
                    : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-sec)] dark:border-[var(--rule)] dark:bg-[var(--surface)]'
                }`}
              >
                {s.options.map((o) => (
                  <option key={o.value} value={o.value} disabled={'disabled' in o ? Boolean(o.disabled) : undefined}>{o.label}</option>
                ))}
              </Select>
              )}

              {!isOverridden && (
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] truncate">{s.effective.effective}</span>
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] shrink-0">via {s.effective.source === 'global' ? (s.effective.globalLabel ?? 'workspace') : s.effective.source}</span>
                </span>
              )}

              {s.warning && (
                <span className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">{s.warning}</span>
              )}
            </div>
          );
        })}
        {/* S-33: may the ranking order and cut line change after the run? */}
        <RankOrderChangeSetting
          scopeKind="track"
          scopeId={track.id}
          ids={{ workspaceId: trackPolicyWorkspaceId, workflowId: cycle?.workflowId ?? above?.workflow?.id ?? null, cycleId: track.cycleId ?? null, trackId: track.id }}
          canEdit={canConfigureTrack}
        />
        <RoomRulesSettings
          scopeKind="track"
          scopeId={track.id}
          ids={{ workspaceId: trackPolicyWorkspaceId, workflowId: cycle?.workflowId ?? above?.workflow?.id ?? null, cycleId: track.cycleId ?? null, trackId: track.id }}
          canEdit={canConfigureTrack}
        />
        {saveError && <p role="alert" className="px-4 py-2 text-xs text-[var(--coral)]">{saveError}</p>}
      </div>

      {/* Collapsible inheritance detail */}
      <div className="border-t border-[var(--rule)] dark:border-[var(--rule)]">
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="w-full px-4 py-2 flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
        >
          <Info className="h-3 w-3" />
          Inheritance chain
          {showDetails ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
        </button>
        {showDetails && (
          <div className="px-4 pb-4 pt-0.5">
            <SessionDefaultsInheritanceChainRows
              depth="track"
              rows={settings.map((s) => ({
                key: s.key,
                label: s.label,
                layer: s.effective,
              }))}
            />
          </div>
        )}
      </div>


      {/* Override permissions: drawn only for someone who may change them */}
      {canConfigureTrack && (
        <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3 bg-[var(--surface)]/50 dark:bg-[var(--surface)]">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-sec)]" />
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide">Override permissions</span>
          </div>
          <div className="space-y-3">
            <ToggleRow
              label="Allow sessions to override these settings"
              description="When disabled, track defaults are locked — session facilitators cannot change them."
              checked={allowSessionOverride}
              disabled={busy === 'allowSessionOverride'}
              onChange={handleAllowSessionOverrideChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared toggle row ────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer group">
      <div>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] group-hover:text-[var(--text-pri)] dark:group-hover:text-white transition-colors">
          {label}
        </p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
          checked ? 'border-[var(--slate)] bg-[var(--slate)]' : 'border-[var(--rule)] bg-[var(--hover-tint)]'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-[var(--bg-raised)] shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

export default TrackSessionDefaultsSection;
